import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, ArrowLeft, Phone, Envelope, ArrowRight, X, Check, CaretRight, CaretLeft, CaretUp, CaretDown, DotsSixVertical, PhoneSlash, PhoneIncoming, Clock } from '@phosphor-icons/react';
import WhatsAppLink from '@/components/WhatsAppLink';
import ScopeToggle from '@/components/ScopeToggle';
import useScopeFilter from '@/hooks/useScopeFilter';
import { PersonLink } from '@/components/PersonLink';
import PageSizeSelect, { loadKanbanColPageSize, saveKanbanColPageSize } from '@/components/PageSizeSelect';

const TIPO_LABELS = { CS: 'Consultores', BC: 'B2C', BB: 'B2B' };

// Ordenação por data da lead, por coluna. Guardada em localStorage para que a
// preferência do consultor persista entre sessões.
const COL_SORT_KEY = 'kanbanColSort';

const loadColSort = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(COL_SORT_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch (_e) { return {}; }
};

const saveColSort = (map) => {
  try { localStorage.setItem(COL_SORT_KEY, JSON.stringify(map)); } catch (_e) { /* ignora */ }
};

// `DataRegistoISO` vem do backend (126 = ISO). Fallback: dd-mm-yyyy.
const leadTime = (lead) => {
  const iso = lead.DataRegistoISO;
  if (iso) {
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) return t;
  }
  const m = String(lead.DataRegisto || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return Date.parse(`${m[3]}-${m[2]}-${m[1]}`);
  return 0;
};

// Regista uma tentativa de contacto no backend (best-effort). Não bloqueia
// a UI — se falhar apenas loga em consola. É usado tanto pelos botões
// rápidos "Não atendeu / Atendeu" como pelos ícones de tel/WA/email que
// já existiam (auto-log em segundo plano).
async function logAttempt(idNegocio, tipo, obs) {
  try {
    await api.post(`/negocios/${idNegocio}/attempt`, { tipo, obs: obs || null });
  } catch (e) {
    console.warn('logAttempt failed', tipo, e?.message);
  }
}

function AIPriorityBadge({ score, level, tldr, idNegocio }) {
  if (score === null || score === undefined) return null;
  const s = Number(score) || 0;
  // Cor por nível (do LLM) ou por score se level ausente
  const isHigh = level === 'high' || s >= 70;
  const isMed  = level === 'medium' || (s >= 40 && s < 70);
  const cls = isHigh
    ? 'bg-red-100 text-red-700 border-red-200'
    : isMed
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-emerald-100 text-emerald-700 border-emerald-200';
  const label = isHigh ? 'Alta' : isMed ? 'Média' : 'Baixa';
  return (
    <span data-testid={`lead-ai-badge-${idNegocio}`}
      title={tldr ? `IA: ${tldr}` : `Prioridade IA: ${label} (${s})`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      IA {s}
    </span>
  );
}

function LeadCard({ lead, onEvolve, onDragStart, onNavigate, attemptSummary, onQuickAttempt, onAnswered }) {
  const [busy, setBusy] = useState(null);
  const [noAnswerOpen, setNoAnswerOpen] = useState(false);
  const [chipsSel, setChipsSel] = useState(new Set());
  const [chipsCatalog, setChipsCatalog] = useState([]);
  const attemptsCount = attemptSummary?.count || 0;

  // Carrega o catálogo de chips só quando o popover é aberto (lazy).
  useEffect(() => {
    if (!noAnswerOpen || chipsCatalog.length > 0) return;
    api.get('/crm/botoes?contexto=lead_evolve')
      .then((r) => setChipsCatalog((r.data?.botoes || []).filter(b => b.Activo)))
      .catch(() => setChipsCatalog([
        { Codigo: 'email', Label: 'Enviei email' },
        { Codigo: 'whatsapp', Label: 'Enviei WhatsApp' },
        { Codigo: 'sms', Label: 'Enviei SMS' },
      ]));
  }, [noAnswerOpen, chipsCatalog.length]);

  const toggleChip = (k) => {
    setChipsSel((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const confirmNoAnswer = async () => {
    setBusy('no_answer');
    try {
      const labels = chipsCatalog.filter(c => chipsSel.has(c.Codigo)).map(c => `[${c.Label}]`);
      const obs = labels.length > 0
        ? `Não atendeu — ${labels.join(' ')}`
        : null;  // deixa o backend usar o label default
      await onQuickAttempt(lead, 'no_answer', obs);
      setNoAnswerOpen(false);
      setChipsSel(new Set());
    } finally { setBusy(null); }
  };

  return (
    <div data-testid={`lead-card-${lead.ID_Negocio}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify({ ID_Negocio: lead.ID_Negocio }));
        onDragStart(lead);
      }}
      className="bg-white border border-[#E5E7EB] rounded-lg p-3 shadow-sm hover:shadow-md hover:border-[#0F1829]/30 transition-all cursor-grab active:cursor-grabbing group"
      onClick={() => onEvolve(lead)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <DotsSixVertical size={14} className="text-[#D1D5DB] group-hover:text-[#9CA3AF] flex-shrink-0" />
          <a href={`/negocios/${lead.ID_Negocio}`} onClick={e => { e.stopPropagation(); onNavigate(lead.ID_Negocio); }}
            className="text-xs font-mono font-medium text-[#0F1829] hover:underline cursor-pointer flex-shrink-0">#{lead.ID_Negocio}</a>
          <p className="text-sm font-semibold text-[#111827] leading-tight truncate">{lead.NomeContacto || lead.Descricao || '-'}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {attemptsCount > 0 && (
            <span data-testid={`lead-attempts-badge-${lead.ID_Negocio}`}
              title={`${attemptsCount} tentativa${attemptsCount > 1 ? 's' : ''} de contacto registada${attemptsCount > 1 ? 's' : ''}`}
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">
              <Clock size={9} weight="bold" /> {attemptsCount}
            </span>
          )}
          <AIPriorityBadge score={lead.AIPriorityScore} level={lead.AIPriorityLevel} tldr={lead.AITldr} idNegocio={lead.ID_Negocio} />
          <CaretRight size={14} className="text-[#9CA3AF] group-hover:text-[#0F1829] mt-0.5 transition-colors" />
        </div>
      </div>
      {lead.ClienteNome && (
        <p className="text-xs text-[#6B7280] mt-1 truncate ml-5" onClick={(e) => e.stopPropagation()}>
          <PersonLink id={lead.ID_Cliente} name={lead.ClienteNome} />
        </p>
      )}
      {lead.IsOwn === 0 && lead.ConsultorNome && (
        <p className="text-[10px] text-amber-700 mt-0.5 ml-5 truncate" title={`Consultor: ${lead.ConsultorNome.trim()}`}
          data-testid={`lead-kanban-via-${lead.ID_Negocio}`}>
          via {lead.ConsultorNome.trim()}
        </p>
      )}
      {lead.CampanhaDesc && (
        <div className="mt-1.5 ml-5">
          <span data-testid={`lead-kanban-campanha-${lead.ID_Negocio}`}
            title={lead.CampanhaId ? `Landing Page: ${lead.CampanhaId}` : ''}
            className="inline-block max-w-full text-[10px] font-medium text-[#0F1829] bg-[#0F1829]/8 px-1.5 py-0.5 rounded truncate">
            {lead.CampanhaDesc}
          </span>
        </div>
      )}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-[#9CA3AF] ml-5">
        <span>{lead.DataRegisto}</span>
        {lead.ValorPrevisto > 0 && <span className="font-mono font-medium text-[#111827]">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(lead.ValorPrevisto)}</span>}
      </div>
      <div className="flex flex-col gap-1 mt-2 ml-5">
        {lead.TelemovelContacto && (
          <span className="inline-flex items-center gap-1.5">
            <a href={`tel:${lead.TelemovelContacto}`}
              onClick={e => { e.stopPropagation(); logAttempt(lead.ID_Negocio, 'call'); }}
              title={lead.TelemovelContacto}
              data-testid={`lead-kanban-phone-${lead.ID_Negocio}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#0F1829] hover:underline">
              <Phone size={11} weight="bold" className="text-[#00C853]" /> {lead.TelemovelContacto}
            </a>
            <WhatsAppLink
              phone={lead.TelemovelContacto}
              idNegocio={lead.ID_Negocio}
              firstName={(lead.NomeContacto || '').split(' ')[0] || ''}
              size={10} className="w-5 h-5"
              onOpen={() => logAttempt(lead.ID_Negocio, 'whatsapp')} />
          </span>
        )}
        {lead.EmailContacto && (
          <a href={`mailto:${lead.EmailContacto}`}
            onClick={e => { e.stopPropagation(); logAttempt(lead.ID_Negocio, 'email'); }}
            title={lead.EmailContacto}
            className="inline-flex items-center gap-1.5 text-[11px] text-[#6B7280] hover:text-[#0F1829] hover:underline truncate">
            <Envelope size={11} /> <span className="truncate">{lead.EmailContacto}</span>
          </a>
        )}
      </div>

      {/* Acções rápidas — "Não atendeu" abre popover com chips (Enviei email,
          Enviei WhatsApp, etc.) para o consultor poder marcar múltiplas
          situações numa só tentativa. "Atendeu" abre o modal de evolução
          já pré-preenchido. */}
      <div className="flex items-center gap-1.5 mt-2 ml-5 relative" data-testid={`lead-quick-actions-${lead.ID_Negocio}`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setNoAnswerOpen(v => !v); }}
          disabled={!!busy}
          data-testid={`lead-quick-no-answer-${lead.ID_Negocio}`}
          title="Registar tentativa: não atendeu"
          className={
            `inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border transition ` +
            (noAnswerOpen
              ? 'bg-red-50 border-red-300 text-red-700'
              : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-40')
          }
        >
          {busy === 'no_answer' ? <SpinnerGap size={10} className="animate-spin" /> : <PhoneSlash size={10} />}
          Não atendeu
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAnswered(lead); }}
          data-testid={`lead-quick-answered-${lead.ID_Negocio}`}
          title="Atendeu / falei — evoluir com resultado pré-preenchido"
          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-[#E5E7EB] bg-white text-[#6B7280] hover:border-green-300 hover:bg-green-50 hover:text-green-700 transition"
        >
          <PhoneIncoming size={10} /> Atendeu
        </button>

        {noAnswerOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid={`lead-noanswer-popover-${lead.ID_Negocio}`}
            className="absolute left-0 top-full mt-1.5 z-30 bg-white border border-[#E5E7EB] rounded-lg shadow-xl p-2.5 w-72"
          >
            <p className="text-[10px] uppercase tracking-widest text-[#7A5B14] font-bold mb-1.5">
              Não atendeu — adicionar situações
            </p>
            <div className="flex flex-wrap gap-1 mb-2">
              {chipsCatalog.length === 0 && (
                <span className="text-[10px] text-[#B8B0A0] italic">a carregar…</span>
              )}
              {chipsCatalog.map(c => {
                const on = chipsSel.has(c.Codigo);
                return (
                  <button
                    key={c.Codigo}
                    type="button"
                    onClick={() => toggleChip(c.Codigo)}
                    data-testid={`noanswer-chip-${lead.ID_Negocio}-${c.Codigo}`}
                    className={
                      `text-[10px] px-1.5 py-0.5 rounded-full border transition ` +
                      (on
                        ? 'bg-[#0F1829] text-white border-[#0F1829]'
                        : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0F1829]/40')
                    }
                  >
                    {on ? '✓ ' : '+ '}{c.Label}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-1.5">
              <button
                type="button"
                onClick={() => { setNoAnswerOpen(false); setChipsSel(new Set()); }}
                data-testid={`noanswer-cancel-${lead.ID_Negocio}`}
                className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded text-[#6B7280]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmNoAnswer}
                disabled={!!busy}
                data-testid={`noanswer-confirm-${lead.ID_Negocio}`}
                className="text-[10px] px-2.5 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-1"
              >
                {busy === 'no_answer' ? <SpinnerGap size={10} className="animate-spin" /> : null}
                Registar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function EvolvePanel({ lead, events, results, onClose, onSubmit, submitting, preselectedState, preselectedResult, errorMsg }) {
  const [form, setForm] = useState({
    ID_Evento: preselectedState || '',
    ID_Resultado: preselectedResult || '1',
    Observacoes: '',
    ID_Encomenda: '',
  });
  // Estados permitidos: pede ao backend a lista alcançável a partir do
  // estado activo (via `lead.Tdom_EventoPossivel`). Enquanto carrega usa
  // o filtro linear como fallback para não deixar o dropdown vazio.
  const [nextEvents, setNextEvents] = useState(null);
  useEffect(() => {
    let ignore = false;
    if (!lead?.ID_Negocio) return () => {};
    api.post('/leads/nextEvents', { ID_Negocio: lead.ID_Negocio })
      .then((r) => { if (!ignore) setNextEvents(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!ignore) setNextEvents([]); });
    return () => { ignore = true; };
  }, [lead?.ID_Negocio]);
  // Chips dinâmicos vindos da tabela `Tdom_BotaoCrm` (gerida pelo admin).
  // Fallback aos 5 hardcoded caso a API falhe / esteja vazia — garante que
  // o Kanban continua funcional se a BD estiver indisponível.
  const [chips, setChips] = useState(new Set());
  const [CHIPS, setChipsList] = useState([
    { key: 'email',       label: 'Enviei email' },
    { key: 'whatsapp',    label: 'Enviei WhatsApp' },
    { key: 'sms',         label: 'Enviei SMS' },
    { key: 'meeting_set', label: 'Reunião agendada' },
    { key: 'no_show',     label: 'Não compareceu' },
  ]);
  useEffect(() => {
    let ignore = false;
    api.get('/crm/botoes?contexto=lead_evolve')
      .then((r) => {
        if (ignore) return;
        const list = (r.data?.botoes || [])
          .filter((b) => b.Activo)
          .map((b) => ({ key: b.Codigo, label: b.Label }));
        if (list.length > 0) setChipsList(list);
      })
      .catch(() => { /* mantém fallback */ });
    return () => { ignore = true; };
  }, []);
  const toggleChip = (k) => {
    setChips((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const currentOrdem = lead.OrdemAtual || 0;
  // Preferência: transições oficialmente mapeadas (backend); fallback ao
  // filtro linear enquanto a chamada está em curso ou se falhar.
  const validEvents = nextEvents !== null
    ? nextEvents
    : events.filter(e => e.Ordem > currentOrdem);

  const handleSubmit = () => {
    if (!form.ID_Evento) return;
    const now = new Date();
    const later = new Date(now.getTime() + 15 * 60000);
    const pad = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;

    // Compõe observações finais: chips (marcadores) + texto livre
    const selectedLabels = CHIPS.filter(c => chips.has(c.key)).map(c => `[${c.label}]`);
    const composed = [selectedLabels.join(' '), form.Observacoes.trim()]
      .filter(Boolean)
      .join(' — ')
      .trim();

    onSubmit({
      ...form,
      Observacoes: composed,
      ID_Lead: lead.ID_Negocio,
      NomePessoa: lead.NomeContacto || '',
      ContactoPessoa: lead.TelemovelContacto || '',
      DataInicio: pad(now),
      DataFim: pad(later),
    });
  };

  const selectedEvent = validEvents.find(e => String(e.ID_EstadoNegocio) === String(form.ID_Evento));
  // Estados que tipicamente exigem ID_Encomenda (Fase Final / Compra Pack / Arquivo / Encerrado).
  // Inclui qualquer estado cuja Ordem >= 99 (regra SP `sp_createContactForLead`).
  const requiresOrder = selectedEvent && selectedEvent.Ordem >= 99;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#111827]">Evoluir Lead</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">{lead.NomeContacto || lead.Descricao || `#${lead.ID_Negocio}`}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-[#F3F4F6] text-[#6B7280] font-medium">{lead.EstadoAtual}</span>
            <ArrowRight size={14} className="text-[#9CA3AF]" />
            <span className="px-2 py-1 rounded bg-[#0F1829]/10 text-[#0F1829] font-medium">
              {selectedEvent ? selectedEvent.Descricao : '—'}
            </span>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1">Próximo Estado</label>
            <select data-testid="evolve-state" value={form.ID_Evento} onChange={e => setForm({...form, ID_Evento: e.target.value})}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none">
              <option value="">Selecionar estado...</option>
              {validEvents.map(e => <option key={e.ID_EstadoNegocio} value={e.ID_EstadoNegocio}>{e.Descricao}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1">Resultado</label>
            <select data-testid="evolve-result" value={form.ID_Resultado} onChange={e => setForm({...form, ID_Resultado: e.target.value})}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none">
              {results.map(r => <option key={r.Id} value={r.Id}>{r.Descricao}</option>)}
            </select>
          </div>
          {requiresOrder && (
            <div>
              <label className="text-xs font-medium text-[#374151] block mb-1">
                ID Encomenda <span className="text-[#9CA3AF] font-normal">(opcional)</span>
              </label>
              <input data-testid="evolve-order" type="text" value={form.ID_Encomenda}
                onChange={e => setForm({...form, ID_Encomenda: e.target.value.replace(/\D/g, '')})}
                placeholder="Ex: 70374"
                className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
              <p className="text-[11px] text-[#6B7280] mt-1">Recomendado para estados de Fase Final (Compra do Pack, Arquivo, Encerrado). O sistema valida do lado do servidor.</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1.5">
              Situações rápidas
              <span className="text-[#9CA3AF] font-normal ml-1">(clica para adicionar às Observações)</span>
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="evolve-chips">
              {CHIPS.map((c) => {
                const on = chips.has(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => toggleChip(c.key)}
                    data-testid={`evolve-chip-${c.key}`}
                    className={
                      `text-[11px] px-2 py-1 rounded-full border transition ` +
                      (on
                        ? 'bg-[#0F1829] text-white border-[#0F1829]'
                        : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0F1829]/40 hover:text-[#0F1829]')
                    }
                  >
                    {on ? '✓ ' : '+ '}{c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] block mb-1">Observações</label>
            <textarea data-testid="evolve-obs" value={form.Observacoes} onChange={e => setForm({...form, Observacoes: e.target.value})}
              rows={2} className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none"
              placeholder="Notas adicionais…" />
            {chips.size > 0 && (
              <p className="text-[10px] text-[#6B7280] mt-1">
                Vai ficar registado: <span className="font-mono">
                  {CHIPS.filter(c => chips.has(c.key)).map(c => `[${c.label}]`).join(' ')}
                  {form.Observacoes.trim() && ` — ${form.Observacoes.trim()}`}
                </span>
              </p>
            )}
          </div>
          {errorMsg && (
            <div data-testid="evolve-error" className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
              <X size={14} className="flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#F3F4F6] rounded-md">Cancelar</button>
          <button data-testid="evolve-submit" disabled={!form.ID_Evento || submitting} onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
            {submitting ? <SpinnerGap size={14} className="animate-spin" /> : <Check size={14} />}
            Evoluir
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeadKanbanPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [tipoLead, setTipoLead] = useState('CS');
  const [evolvingLead, setEvolvingLead] = useState(null);
  const [preselectedState, setPreselectedState] = useState('');
  const [preselectedResult, setPreselectedResult] = useState('1');
  const [attemptCounts, setAttemptCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [evolveError, setEvolveError] = useState(null);
  const [draggingLead, setDraggingLead] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [scope, setScope] = useScopeFilter('all');
  const [aiFilter, setAiFilter] = useState('all'); // 'all' | 'high' | 'medium-plus'
  const [sortByAi, setSortByAi] = useState(false);
  // Página actual por coluna (id_estado → nº de página). Reset quando os
  // dados são recarregados ou o filtro muda — o utilizador espera voltar
  // ao topo de cada pilha depois de mudar o tipo/scope.
  const [colPages, setColPages] = useState({});
  const [colSort, setColSort] = useState(loadColSort);
  const [colPageSize, setColPageSize] = useState(loadKanbanColPageSize);
  // Mapa de transições permitidas ({ origem: [destino,...] }) usado pelo
  // drag-drop para iluminar só as colunas alcançáveis a partir do estado
  // actual da lead arrastada.
  const [transitions, setTransitions] = useState({});

  const loadData = (tipo) => {
    setLoading(true);
    Promise.all([
      api.post('/leads/kanban', { TipoLead: tipo, scope }).then(r => setLeads(Array.isArray(r.data) ? r.data : [])),
      api.post('/leads/events', { TipoLead: tipo }).then(r => setEvents(Array.isArray(r.data) ? r.data : [])),
      api.post('/leads/results').then(r => setResults(Array.isArray(r.data) ? r.data : [])),
      api.post('/leads/eventTransitions', { TipoLead: tipo })
        .then(r => setTransitions(r.data && typeof r.data === 'object' ? r.data : {}))
        .catch(() => setTransitions({})),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(tipoLead); setColPages({}); }, [tipoLead, activeId, scope]);

  // Carrega contadores de tentativas para os IDs visíveis (batch).
  // É best-effort — se Mongo falhar o Kanban continua a funcionar sem badges.
  const loadAttemptCounts = useCallback(async (leadRows) => {
    const ids = (leadRows || []).map(l => l.ID_Negocio).filter(Boolean);
    if (ids.length === 0) { setAttemptCounts({}); return; }
    try {
      const r = await api.post('/leads/attempts/counts', { ids });
      setAttemptCounts(r.data?.counts || {});
    } catch (_e) { /* silencioso — não bloqueia UI */ }
  }, []);

  useEffect(() => { loadAttemptCounts(leads); }, [leads, loadAttemptCounts]);

  // Handler: acção rápida "Não atendeu" — apenas regista tentativa, não
  // altera estado. Actualiza contador local imediatamente (optimistic).
  // Aceita `obs` opcional que resulta dos chips do popover.
  const handleQuickAttempt = async (lead, kind, obs = null) => {
    const tipo = kind === 'no_answer' ? 'call_no_answer' : kind === 'answered' ? 'call_answered' : kind;
    setAttemptCounts(prev => ({
      ...prev,
      [lead.ID_Negocio]: { count: (prev[lead.ID_Negocio]?.count || 0) + 1, last_ts: new Date().toISOString() },
    }));
    try {
      const r = await api.post(`/negocios/${lead.ID_Negocio}/attempt`,
        obs ? { tipo, obs } : { tipo });
      if (r.data?.success) {
        setToast({ type: 'success', msg: kind === 'no_answer' ? 'Tentativa registada — Não atendeu' : 'Tentativa registada' });
      } else {
        setToast({ type: 'error', msg: 'Não foi possível registar a tentativa' });
      }
    } catch (_e) {
      setToast({ type: 'error', msg: 'Falha de comunicação ao registar tentativa' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  // Handler: botão "Atendeu" → abre modal com resultado=3 pré-preenchido
  const handleAnsweredShortcut = (lead) => {
    setPreselectedState('');
    setPreselectedResult('3');  // 3 = Atendeu
    setEvolveError(null);
    setEvolvingLead(lead);
    // Regista a tentativa "atendeu" em paralelo, no fundo
    logAttempt(lead.ID_Negocio, 'call_answered');
    // Actualiza contador local (optimistic)
    setAttemptCounts(prev => ({
      ...prev,
      [lead.ID_Negocio]: { count: (prev[lead.ID_Negocio]?.count || 0) + 1, last_ts: new Date().toISOString() },
    }));
  };

  const columns = useMemo(() => {
    // "Registo" é o estado inicial das leads criadas via formulários de
    // consultores/webhooks — devem aparecer no kanban para serem tratadas
    // (dropped previously). "Pré-Registo" fica excluído (lead pre-lead).
    const stateOrder = events
      .filter(e => !e.Descricao.startsWith('Encerrado') && !e.Descricao.startsWith('Arquivo') && !e.Descricao.startsWith('Pré-Registo'))
      .sort((a, b) => a.Ordem - b.Ordem);
    // Filtro por prioridade IA
    const matchesAi = (l) => {
      if (aiFilter === 'all') return true;
      const level = l.AIPriorityLevel;
      const score = Number(l.AIPriorityScore) || 0;
      if (aiFilter === 'high') return level === 'high' || score >= 70;
      if (aiFilter === 'medium-plus') return level === 'high' || level === 'medium' || score >= 40;
      return true;
    };
    return stateOrder.map(s => {
      let colLeads = leads.filter(l => String(l.ID_EstadoAtual) === String(s.ID_EstadoNegocio) && matchesAi(l));
      const dir = colSort[String(s.ID_EstadoNegocio)];
      if (dir === 'asc' || dir === 'desc') {
        const mult = dir === 'asc' ? 1 : -1;
        colLeads = [...colLeads].sort((a, b) => (leadTime(a) - leadTime(b)) * mult);
      } else if (sortByAi) {
        colLeads = [...colLeads].sort((a, b) =>
          (Number(b.AIPriorityScore) || -1) - (Number(a.AIPriorityScore) || -1)
        );
      }
      return {
        id: s.ID_EstadoNegocio,
        label: s.Descricao.replace(/ - b2c$/, '').replace(/ - Cons$/, ''),
        fullLabel: s.Descricao,
        ordem: s.Ordem,
        leads: colLeads,
      };
    });
  }, [events, leads, aiFilter, sortByAi, colSort]);

  // Reset paginação quando o filtro IA muda (senão o utilizador pode ficar
  // preso numa página que já não existe).
  useEffect(() => { setColPages({}); }, [aiFilter, sortByAi]);

  const setColPage = useCallback((colId, p) => {
    setColPages(prev => ({ ...prev, [colId]: Math.max(1, p) }));
  }, []);

  // Alterna a ordenação por data da coluna: sem ordem → mais recentes → mais
  // antigas → sem ordem (volta à ordem original do servidor).
  const cycleColSort = useCallback((colId) => {
    const key = String(colId);
    setColSort(prev => {
      const cur = prev[key];
      const next = { ...prev };
      if (cur === 'desc') next[key] = 'asc';
      else if (cur === 'asc') delete next[key];
      else next[key] = 'desc';
      saveColSort(next);
      return next;
    });
    setColPages(prev => ({ ...prev, [key]: 1 }));
  }, []);

  const isValidDrop = (col) => {
    if (!draggingLead) return false;
    // Se o mapa de transições está disponível para o estado actual, usa-o.
    // Caso contrário, cai no filtro linear por Ordem (fallback seguro).
    const origem = String(draggingLead.ID_EstadoAtual ?? '');
    const allowed = transitions[origem];
    if (Array.isArray(allowed) && allowed.length > 0) {
      return allowed.map(String).includes(String(col.id));
    }
    return col.ordem > (draggingLead.OrdemAtual || 0);
  };

  const handleDragOver = (e, col) => {
    if (!isValidDrop(col)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col.id);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverCol(null);
  };

  const handleDrop = (e, col) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!draggingLead || !isValidDrop(col)) return;
    setPreselectedState(String(col.id));
    setEvolveError(null);
    setEvolvingLead(draggingLead);
    setDraggingLead(null);
  };

  const handleDragEnd = () => {
    setDraggingLead(null);
    setDragOverCol(null);
  };

  useEffect(() => {
    const end = () => { setDraggingLead(null); setDragOverCol(null); };
    document.addEventListener('dragend', end);
    return () => document.removeEventListener('dragend', end);
  }, []);

  const handleEvolve = async (formData) => {
    setSubmitting(true);
    setEvolveError(null);
    try {
      const r = await api.post(`/negocios/${formData.ID_Lead}/addState`, {
        ID_Evento: formData.ID_Evento,
        ID_Resultado: parseInt(formData.ID_Resultado),
        NomePessoa: formData.NomePessoa,
        ContactoPessoa: formData.ContactoPessoa,
        Observacoes: formData.Observacoes,
        DataInicio: formData.DataInicio,
        DataFim: formData.DataFim,
        ValorPrevisto: 0,
        ID_Encomenda: formData.ID_Encomenda || '',
      });
      if (r.data?.success) {
        setToast({ type: 'success', msg: 'Lead evoluída com sucesso!' });
        setEvolvingLead(null);
        setPreselectedState('');
        loadData(tipoLead);
      } else {
        // Mantém o modal aberto e mostra o erro INLINE (não desaparece).
        // Também emite o toast como reforço visual.
        const msg = r.data?.message || 'Erro ao evoluir lead';
        setEvolveError(msg);
        setToast({ type: 'error', msg });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro de comunicação';
      setEvolveError(msg);
      setToast({ type: 'error', msg });
    }
    setSubmitting(false);
    setTimeout(() => setToast(null), 4000);
  };

  const openEvolveFromClick = (lead) => {
    setPreselectedState('');
    setPreselectedResult('1');
    setEvolveError(null);
    setEvolvingLead(lead);
  };

  return (
    <div data-testid="kanban-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button data-testid="back-btn" onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={16} /> Voltar a Leads
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Tratar Leads
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Arraste um card para a próxima coluna ou clique para evoluir</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate('/leads/foco')} data-testid="kanban-foco-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-[#0F1829] text-white hover:bg-[#002480] transition"
            title="Modo Foco: uma lead de cada vez">
            🎯 Modo Foco
          </button>
          <div className="flex items-center gap-1.5" data-testid="ai-filter-group">
            <label className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wider">IA</label>
            <select data-testid="ai-filter-select" value={aiFilter} onChange={(e) => setAiFilter(e.target.value)}
              className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none">
              <option value="all">Todas</option>
              <option value="high">Só Alta</option>
              <option value="medium-plus">Média+</option>
            </select>
            <button data-testid="ai-sort-toggle" onClick={() => setSortByAi(v => !v)}
              title={sortByAi ? 'A ordenar por score IA' : 'Ordenar por score IA'}
              className={`text-[11px] font-medium px-2 py-1.5 rounded-md border transition-colors ${
                sortByAi
                  ? 'bg-[#0F1829] text-white border-[#0F1829]'
                  : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}>
              ↓ Score
            </button>
          </div>
          <ScopeToggle value={scope} onChange={setScope} testid="kanban-scope-toggle" />
          <PageSizeSelect value={colPageSize} testId="kanban-col-page-size"
            label="Por coluna:"
            onChange={(v) => { saveKanbanColPageSize(v); setColPageSize(v); setColPages({}); }} />
          <div className="flex items-center bg-[#F3F4F6] rounded-lg p-0.5" data-testid="tipo-tabs">
            {Object.entries(TIPO_LABELS).map(([k, v]) => (
              <button key={k} onClick={() => { setTipoLead(k); }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${tipoLead === k
                  ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {toast.type === 'success' ? <Check size={16} /> : <X size={16} />}
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {columns.map(col => {
            const valid = isValidDrop(col);
            const isOver = dragOverCol === col.id;
            const totalInCol = col.leads.length;
            const totalPages = Math.max(1, Math.ceil(totalInCol / colPageSize));
            const curPage = Math.min(colPages[col.id] || 1, totalPages);
            const startIdx = (curPage - 1) * colPageSize;
            const visibleLeads = col.leads.slice(startIdx, startIdx + colPageSize);
            return (
              <div key={col.id}
                className={`flex-shrink-0 w-[280px] transition-all duration-200 ${draggingLead ? (valid ? 'opacity-100' : 'opacity-40') : ''}`}
                data-testid={`col-${col.id}`}
                onDragOver={(e) => handleDragOver(e, col)}
                onDragEnter={(e) => { if (valid) { e.preventDefault(); setDragOverCol(col.id); }}}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col)}>
                <div className={`rounded-t-lg px-3 py-2.5 flex items-center justify-between transition-colors duration-200 ${
                  isOver ? 'bg-[#0F1829] text-white' : 'bg-[#F3F4F6]'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide truncate ${isOver ? 'text-white' : 'text-[#374151]'}`} title={col.fullLabel}>
                    {col.label}
                  </h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => cycleColSort(col.id)}
                      data-testid={`col-${col.id}-sort-date`}
                      title={
                        colSort[String(col.id)] === 'desc' ? 'Data: mais recentes primeiro (clique para inverter)'
                          : colSort[String(col.id)] === 'asc' ? 'Data: mais antigas primeiro (clique para remover a ordenação)'
                            : 'Ordenar por data da lead'
                      }
                      aria-label="Ordenar por data da lead"
                      className={`h-5 px-1 inline-flex items-center gap-0.5 rounded border text-[9px] font-bold transition-colors ${
                        colSort[String(col.id)]
                          ? 'bg-[#0F1829] text-white border-[#0F1829]'
                          : isOver
                            ? 'bg-white/15 text-white border-white/40'
                            : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0F1829]/40 hover:text-[#0F1829]'}`}>
                      {colSort[String(col.id)] === 'asc'
                        ? <CaretUp size={9} weight="bold" />
                        : <CaretDown size={9} weight="bold" />}
                      DATA
                    </button>
                    <span className={`text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ${
                      isOver ? 'bg-white text-[#0F1829]' : 'bg-[#6B7280] text-white'}`}>
                      {totalInCol}
                    </span>
                  </div>
                </div>
                <div className={`rounded-b-lg border border-t-0 min-h-[120px] p-2 space-y-2 transition-all duration-200 ${
                  isOver ? 'bg-[#0F1829]/5 border-[#0F1829] ring-2 ring-[#0F1829]/20' : 'bg-[#F9FAFB] border-[#E5E7EB]'}`}>
                  {isOver && totalInCol === 0 && (
                    <div className="flex items-center justify-center py-6">
                      <span className="text-xs font-medium text-[#0F1829]">Largar aqui</span>
                    </div>
                  )}
                  {totalInCol === 0 && !isOver && (
                    <p className="text-center text-xs text-[#9CA3AF] py-6">Sem leads</p>
                  )}
                  {visibleLeads.map(l => (
                    <LeadCard key={l.ID_Negocio} lead={l} onEvolve={openEvolveFromClick}
                      onDragStart={setDraggingLead} onNavigate={(id) => navigate(`/negocios/${id}`)}
                      attemptSummary={attemptCounts[l.ID_Negocio]}
                      onQuickAttempt={handleQuickAttempt}
                      onAnswered={handleAnsweredShortcut} />
                  ))}
                  {totalPages > 1 && (
                    <div className="pt-2 mt-1 border-t border-[#E5E7EB] flex items-center justify-between text-[10px] text-[#6B7280]"
                      data-testid={`col-${col.id}-pagination`}>
                      <button disabled={curPage <= 1}
                        onClick={() => setColPage(col.id, curPage - 1)}
                        className="w-6 h-6 grid place-items-center rounded border border-[#E5E7EB] bg-white hover:bg-[#F3F4F6] disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`col-${col.id}-page-prev`} aria-label="Página anterior">
                        <CaretLeft size={10} />
                      </button>
                      <span className="font-medium text-[#374151]" data-testid={`col-${col.id}-page-info`}>
                        {curPage} / {totalPages} · {totalInCol} leads
                      </span>
                      <button disabled={curPage >= totalPages}
                        onClick={() => setColPage(col.id, curPage + 1)}
                        className="w-6 h-6 grid place-items-center rounded border border-[#E5E7EB] bg-white hover:bg-[#F3F4F6] disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`col-${col.id}-page-next`} aria-label="Próxima página">
                        <CaretRight size={10} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {columns.length === 0 && (
            <p className="text-sm text-[#6B7280] py-10">Sem estados configurados para este tipo de lead.</p>
          )}
        </div>
      )}

      {!loading && (
        <div className="mt-4 flex items-center gap-4 text-xs text-[#6B7280]">
          <span>{leads.length} leads activas</span>
          {columns.map(col => col.leads.length > 0 && (
            <span key={col.id} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#0F1829]"></span>
              {col.label}: {col.leads.length}
            </span>
          ))}
        </div>
      )}

      {evolvingLead && (
        <EvolvePanel
          lead={evolvingLead}
          events={events}
          results={results}
          onClose={() => { setEvolvingLead(null); setPreselectedState(''); setPreselectedResult('1'); setEvolveError(null); }}
          onSubmit={handleEvolve}
          submitting={submitting}
          preselectedState={preselectedState}
          preselectedResult={preselectedResult}
          errorMsg={evolveError}
        />
      )}
    </div>
  );
}
